import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoryDropdownOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <div className="w-9 h-9 flex items-center justify-center">
            <i className="ri-book-read-line text-2xl" style={{ color: 'oklch(var(--primary-500))' }}></i>
          </div>
          <span className="font-heading text-xl font-bold text-foreground-900">073漫画网</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              location.pathname === '/'
                ? 'text-primary-500 bg-primary-50'
                : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
            }`}
          >
            首页
          </Link>

          <div
            className="relative"
            onMouseEnter={() => setCategoryDropdownOpen(true)}
            onMouseLeave={() => setCategoryDropdownOpen(false)}
          >
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                location.pathname.startsWith('/category')
                  ? 'text-primary-500 bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
              }`}
            >
              分类
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoryDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {categoryDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 bg-background-50 rounded-xl border border-background-200/70 py-2 w-44 shadow-lg">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-base`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/category/shaonian"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              location.pathname === '/category/shaonian'
                ? 'text-primary-500 bg-primary-50'
                : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
            }`}
          >
            少年漫画
          </Link>

          <Link
            to="/category/rexie"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              location.pathname === '/category/rexie'
                ? 'text-primary-500 bg-primary-50'
                : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
            }`}
          >
            热血漫画
          </Link>

          <Link
            to="/about"
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              location.pathname === '/about'
                ? 'text-primary-500 bg-primary-50'
                : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
            }`}
          >
            关于我们
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="搜索漫画..."
              className="pl-9 pr-4 py-2 text-sm bg-background-100 border border-background-200/70 rounded-full text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all w-48"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
            </div>
          </div>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer text-foreground-700 hover:bg-background-100 transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="菜单"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-4 pb-4 animate-in slide-in-from-top-2">
          <div className="flex flex-col gap-1">
            <Link to="/" className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors cursor-pointer">首页</Link>
            <div className="px-4 py-2.5 text-sm font-medium text-foreground-500">分类浏览</div>
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-3 px-6 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-base`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
            <Link to="/about" className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors cursor-pointer">关于我们</Link>
          </div>
          <div className="mt-3 relative">
            <input
              type="text"
              placeholder="搜索漫画..."
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-full text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}